import { defineStore } from 'pinia';
import { ref, computed, reactive } from 'vue';
import { useAPI } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import {
  evaluateSide,
  getDeltas,
  createNewGameFromOldGame,
  convertAmericanToDecimal,
} from '@/core/utils';
import { emitter } from '@/core/emitter';
import {
  mergeUnmatchedIntoUserOrders,
  ownOrderToBookSession,
} from '@/core/userOrdersFromUnmatched';
import { set } from '@vueuse/core';
import {
  matchedVolumeFactsFromUpdate,
  applyMatchedVolumeFacts,
} from '@/utils/matchedVolume';

// PRO-123 (QA R4): ids of games the app removed because they ended or
// expired. These removal sites are the only code that knows WHY a row left
// the store (socket `ended` splice, expiry worker) — pages that re-seed the
// board from cached payloads (AllProps) consult this so a cached snapshot
// never resurrects a game the app saw end. Module-scoped on purpose: it must
// survive route changes and component remounts (a "gone from the store" check
// cannot distinguish an ended-game splice from a route-change wipe). Session-
// bounded, pruned oldest-first so it cannot grow without limit.
// Whether this app session has established that the API predates the combined
// `lobby` homePage variant (it answers 400). useAPI retries any non-timeout
// failure once after a 1s delay AND reports each failure to Sentry, so the
// homepage probes at most once and uses the legacy two-call path thereafter.
// Module-scoped on purpose: `<script setup>` re-runs on every mount, so a flag
// living in the view would reset each time the user navigates back to it.
let lobbyVariantUnavailable = false;
export function isLobbyVariantUnavailable() {
  return lobbyVariantUnavailable;
}
export function markLobbyVariantUnavailable() {
  lobbyVariantUnavailable = true;
}

const REMOVED_GAME_IDS_MAX = 5000;
export const removedGameIds = new Set();
function recordRemovedGame(gameID) {
  if (gameID == null) return;
  removedGameIds.add(String(gameID));
  if (removedGameIds.size > REMOVED_GAME_IDS_MAX) {
    for (const id of removedGameIds) {
      removedGameIds.delete(id);
      if (removedGameIds.size <= REMOVED_GAME_IDS_MAX) break;
    }
  }
}

export const useExchangeStore = defineStore('exchange', () => {
  const userStore = useUserStore();
  const leagues = ref([]);
  const eventSelected = ref('all');
  const sportsNavigations = ref([]);
  let games = ref([]);
  const loadingMoreGames = ref(false);
  const loadingMoreRecentOrders = ref(false);
  const pagination = ref({
    totalDocs: null,
    offset: null,
    limit: 20,
    totalPages: null,
    page: 1,
    pagingCounter: null,
    hasPrevPage: null,
    hasNextPage: null,
    prevPage: null,
    nextPage: null,
    pages: [],
  });
  const oddsComputed = ref('');
  const userOrders = ref([]);
  const singleGame = ref({
    loading: false,
    game: {},
    orderSummary: [],
    childGames: [],
    games: [],
    navigation: [],
  });
  const bestOfferGame = ref(null);
  const bestOfferSession = ref(null);
  // Analytics marker mirroring bestOfferSession: the AllProps page writes it
  // just before opening a bet modal so TakeBetModal can attribute the open to
  // the props page (source + navigation state at click time) without
  // threading props through the whole make-bet emit chain. The modal matches
  // by session object identity, so a stale marker can never mis-attribute a
  // later open of a different session. Shape: { session, category, grouping }.
  const propsBetContext = ref(null);
  const userOrdersLoading = ref(false);
  const bestOffersLoading = ref(false);
  // the values for singleGameOrExchange: singleGame, exchange, sportsbook, loggedIn (for top traded socket udpdates)
  const singleGameOrExchange = ref('');
  const activityFeedMessages = ref([]);
  const unreadActivityFeedMessages = ref(0);
  const activityFeedHistoryPagination = ref({
    totalDocs: null,
    offset: null,
    limit: 20,
    totalPages: null,
    page: 1,
    pagingCounter: null,
    hasPrevPage: null,
    hasNextPage: null,
    prevPage: null,
    nextPage: null,
    pages: [],
  });
  const topTradedOrderbooks = ref([]);
  const bestOffers = ref([]);
  const leagueGames = ref([]);
  const leagueGamesLoading = ref(false);
  let leagueGamesCurrentLeague = null;
  let leagueGamesInflight = null;

  async function fetchLeagueGames(league) {
    if (!league) {
      leagueGames.value = [];
      leagueGamesCurrentLeague = null;
      return [];
    }
    if (leagueGamesCurrentLeague === league && leagueGames.value.length) {
      return leagueGames.value;
    }
    if (leagueGamesInflight && leagueGamesCurrentLeague === league) {
      return leagueGamesInflight;
    }
    leagueGamesCurrentLeague = league;
    leagueGamesLoading.value = true;
    const { request } = useAPI('exchange/v2/getGames');
    leagueGamesInflight = request({ league })
      .then((response) => {
        const list = response?.games ?? [];
        if (leagueGamesCurrentLeague === league) {
          leagueGames.value = list;
        }
        return list;
      })
      .catch(() => {
        if (leagueGamesCurrentLeague === league) {
          leagueGames.value = [];
        }
        return [];
      })
      .finally(() => {
        leagueGamesLoading.value = false;
        leagueGamesInflight = null;
      });
    return leagueGamesInflight;
  }

  const matchType = ref({});

  // ONE fetch of the sport/league nav, shared by everyone who needs it.
  //
  // Three call paths grew up independently — app boot (ApplicationLayout),
  // the post-login refresh (this store's `loadSportsData` listener AND
  // ApplicationLayout's own `logged-in` handler, which duplicated each
  // other), and route/game-update refreshes (getSportsData) — so a single
  // login fired this endpoint three times concurrently. The payload is 556
  // bytes and byte-identical logged out and logged in, so those extra round
  // trips bought nothing while occupying connection slots during exactly the
  // window the homepage lobby call needs them.
  //
  // Concurrent callers share one in-flight promise; a caller arriving within
  // the TTL of a completed fetch reuses it. Mirrors fetchLeagueGames below.
  //
  // `force: true` bypasses BOTH — required by the socket-reconnect recovery
  // path, which exists precisely to re-sync data that may have gone stale
  // while the connection was down. Serving that a cached response would make
  // the recovery silently do nothing.
  const SPORT_LEAGUES_TTL_MS = 30_000;
  let sportLeaguesFetchedAt = 0;
  let sportLeaguesInflight = null;

  async function fetchSportLeagues({ force = false } = {}) {
    // Test for actual CONTENT, not truthiness: sportsNavigations starts as
    // `[]` and reset() puts it back to `[]` on logout — and `[]` is truthy,
    // so a bare check would treat a wiped cache as a hit and serve an empty
    // nav (logout then login inside the TTL = blank sports menu).
    const cached = sportsNavigations.value?.sportsAvailable
      ? sportsNavigations.value
      : null;
    const fresh = Date.now() - sportLeaguesFetchedAt < SPORT_LEAGUES_TTL_MS;
    if (!force && fresh && cached) return cached;
    if (sportLeaguesInflight && !force) return sportLeaguesInflight;

    sportLeaguesInflight = (async () => {
      try {
        const { get } = useAPI('exchange/getSportLeagues');
        const response = await get();
        if (response) {
          sportsNavigations.value = response;
          sportLeaguesFetchedAt = Date.now();
        }
        return sportsNavigations.value;
      } catch {
        // Leave whatever nav we already have; callers treat this as a no-op.
        return sportsNavigations.value;
      } finally {
        sportLeaguesInflight = null;
      }
    })();
    return sportLeaguesInflight;
  }

  // Applies the league list for one sport off the shared nav. Kept separate
  // so each caller can pick its own sport (route param, game update, etc.)
  // without needing its own fetch.
  function applyLeaguesForSport(sport) {
    if (!sport) return;
    const available = sportsNavigations.value?.sportsAvailable;
    if (available && sport in available) {
      leagues.value = available[sport];
    }
  }

  function setMatchType(payload) {
    matchType.value = payload;
  }

  // `force` is threaded through for the socket-reconnect path (see
  // fetchSportLeagues) — everything else shares the deduped fetch.
  async function getSportsData(sport, { force = false } = {}) {
    await fetchSportLeagues({ force });
    applyLeaguesForSport(sport);
  }

  function setSingleGameOrExchange(payload) {
    singleGameOrExchange.value = payload;
  }

  function processGameFromApi(game) {
    // Golf doesn't have totals
    if (
      game.sport !== 'golf' &&
      !Object.keys(game.under).length &&
      !Object.keys(game.over).length
    ) {
      if (game.mainTotal === undefined) {
        game.mainTotal = 0;
      }
      game.under = { [game.mainTotal]: [] };
      game.over = { [game.mainTotal]: [] };
    }

    // Fighting does't have spreads
    if (
      game.sport !== 'fighting' &&
      !Object.keys(game.awaySpreads).length &&
      !Object.keys(game.homeSpreads).length
    ) {
      // Assume the game is a spread game
      if (game.mainAwaySpread === undefined) {
        game.mainAwaySpread = 0;
        game.mainHomeSpread = 0;
      }
      game.awaySpreads = { [game.mainAwaySpread]: [] };
      game.homeSpreads = { [game.mainHomeSpread]: [] };
    }
    return game;
  }

  function pushGame(game) {
    processGameFromApi(game);
    games.value.push(game);
  }

  function shouldUseNewData(sport, newGames) {
    // route sport is the one that was passed into the get orderbook api call
    const sportFromRoute = sport;

    // games sport is the one in the games array returned from api
    const sportFromGames = newGames.length > 0 ? newGames[0].sport : undefined;

    // sport from games currently stored in state at time of function call
    const sportFromOldGames =
      games.value.length > 0 ? games.value[0].sport : [];

    const sportsMatch = sportFromRoute === sportFromGames;
    if (sportsMatch) {
      return true;
    } else {
      // There is a mismatch

      // If route is all, a mismatch is fine
      if (sportFromRoute === 'all') return true;

      // See if old data matches, if so use the old data
      if (sportFromRoute === sportFromOldGames) {
        // Old data matches sport from route, so keep it
        return false;
      } else {
        // Use the new data because the old data also doens't match
        return true;
      }
    }
  }

  function setGames(sport, newGames) {
    // Clear array inplace
    games.value.length = 0;

    if (!shouldUseNewData(sport, newGames)) {
      return;
    }

    newGames.forEach(pushGame);
    emitter.emit('appWorkerUpdates', {
      type: 'UPDATE_GAMES',
      view: '',
      games: newGames,
    });
  }

  function pushGames(newGames) {
    newGames.forEach(pushGame);
    emitter.emit('appWorkerUpdates', {
      type: 'UPDATE_GAMES',
      view: singleGameOrExchange.value,
      games: newGames,
    });
  }

  function updateGame(payload) {
    const { newGame, route = null } = payload;
    if (newGame && newGame.id) {
      const view = singleGameOrExchange.value;
      if (route === 'Logged-in') {
        const oldGameIndex = topTradedOrderbooks.value.findIndex(
          (g) => g.id === newGame.id,
        );
        if (oldGameIndex !== -1) {
          topTradedOrderbooks.value.splice(oldGameIndex, 1, newGame);
        } else if (newGame.live || newGame.league.includes('2H')) {
          topTradedOrderbooks.value.unshift(newGame);
        } else {
          topTradedOrderbooks.value.push(newGame);
        }
      } else if (view === 'singleGame') {
        if (singleGame.value.game && singleGame.value.game.id === newGame.id) {
          singleGame.value.game = newGame;
        }
        const games = singleGame.value.games;
        const oldGameIndex = games.findIndex((g) => g.id === newGame.id);
        if (oldGameIndex !== -1) {
          games.splice(oldGameIndex, 1, newGame);
        }
      } else {
        const oldGameIndex = games.value.findIndex((g) => g.id === newGame.id);
        if (oldGameIndex !== -1) {
          games.value.splice(oldGameIndex, 1, newGame);
        } else if (newGame.live || newGame.league.includes('2H')) {
          games.value.unshift(newGame);
        } else {
          games.value.push(newGame);
        }
      }

      emitter.emit('appWorkerUpdates', {
        type: 'UPDATE_GAMES',
        view,
        games: [newGame],
      });
    }
  }

  function setGameOddsDelta(payload) {
    const { newGame, oddsDelta, currentView } = payload;
    if (newGame && newGame.id) {
      let gameToUpdate;
      if (currentView === 'singleGame') {
        /* if (state.singleGame.game && state.singleGame.game.id === newGame.id) {
          gameToUpdate = state.singleGame.game;
        } */
        gameToUpdate = singleGame.value.games.find((g) => g.id === newGame.id);
      } else {
        gameToUpdate = games.value.find((g) => g.id === newGame.id);
      }
      if (gameToUpdate) {
        gameToUpdate.oddsDelta = oddsDelta;
      }
    }
  }

  function setLeagues(payload) {
    leagues.value = payload;
  }

  const apiGames = computed(() => [...singleGame.value.games]);

  function setSingleGame(payload) {
    const mainGame = {
      ...payload.game,
      type: 'MAIN',
    };

    const childs = payload.childGames.map((game) => ({
      ...game,
      type: game.league,
    }));

    const props = payload.propGames.map((game) => ({
      ...game,
    }));

    const games = [mainGame, ...childs, ...props];
    games.forEach(processGameFromApi);
    const navigation = games.map((g) => g.type);

    singleGame.value = {
      childGames: childs,
      propGames: props,
      games,
      game: null,
      navigation,
      orderSummary: payload.orderSummary,
    };

    singleGame.value.game = apiGames.value[0];

    // Seed userOrders from the HTTP order summary. Position-updates are
    // live-only (no replay on reconnect), so after a socket reconnect or a
    // page load the store would otherwise have no record of the user's open
    // orders and aggregate ownership stamping (green highlight, own-volume
    // subtraction) could never recover. The HTTP summary has the same shape
    // as a position-update payload, so replace-or-insert per game.
    const summaries = Array.isArray(payload.orderSummary)
      ? payload.orderSummary.filter((summary) => summary?.renderedGame?.id)
      : [];
    summaries.forEach((summary) => {
      const index = userOrders.value.findIndex(
        (existing) => existing.renderedGame?.id === summary.renderedGame.id,
      );
      if (index !== -1) {
        userOrders.value.splice(index, 1, summary);
      } else {
        userOrders.value.push(summary);
      }
      refreshAggregateOwnershipForGame(summary.renderedGame.id);
    });

    emitter.emit('appWorkerUpdates', {
      type: 'UPDATE_GAMES',
      view: 'singleGame',
      games: singleGame.value.game ? [singleGame.value.game] : [],
    });
  }

  function setUserOrders(payload) {
    mergeUserOrders(payload);
  }

  // userOrders is the client's only record of which book rows are the user's
  // (the aggregate feed is anonymous; see applyAggregateOwnership). A page's
  // orderSummary covers ONLY that page's games, so replacing the whole array
  // with it forgot every other game the user has orders in -- and each of
  // those rows lost its green stamp on its next price frame ("highlight
  // appears, then vanishes"). Merge per game instead, the way setSingleGame
  // and handlePositionUpdate already do, and re-stamp immediately rather than
  // waiting for the next tick. Games whose orders are all gone are corrected
  // by the user feed's position-update (which carries an empty unmatched
  // list for that game) and by reseedUserOrders, never by a page load.
  function mergeUserOrders(summaries) {
    const list = Array.isArray(summaries)
      ? summaries.filter((summary) => summary?.renderedGame?.id)
      : [];
    if (!Array.isArray(userOrders.value)) userOrders.value = [];
    list.forEach((summary) => {
      const gameID = summary.renderedGame.id;
      const index = userOrders.value.findIndex(
        (existing) => existing?.renderedGame?.id === gameID,
      );
      if (index !== -1) {
        userOrders.value.splice(index, 1, summary);
      } else {
        userOrders.value.push(summary);
      }
      refreshAggregateOwnershipForGame(gameID);
    });
  }

  // Full rebuild of the user's OPEN-order record from the server's truth:
  // user/getUnmatched, the existing flat per-order list of ALL the user's
  // open orders (one indexed Session.find, no per-game rendering on the API),
  // re-derived client-side into the per-game summary shape (see
  // core/userOrdersFromUnmatched). Called at the moments
  // the local record can be wrong or empty -- page load, login, either
  // socket's reconnect (position-updates are live-only, no replay), and tab
  // wake. Only each game's `unmatched` list is rebuilt; matched positions
  // stay as the last page load / position-update left them. Stamps are
  // refreshed for the union of games before and after so rows whose orders
  // disappeared are cleared too. Throttled and single-flight so a reconnect
  // storm cannot hammer the API.
  const USER_ORDERS_RESEED_MIN_INTERVAL_MS = 3000;
  let userOrdersReseedLastCall = 0;
  let userOrdersReseedInFlight = null;

  // gameID -> Date.now() of the last live position-update applied for it.
  // The reseed's HTTP body is a snapshot from when the server built it; a
  // position-update that arrived while the request was in flight is newer
  // than that snapshot for its game. Without this the later-arriving HTTP
  // body would overwrite the fresher live state (e.g. re-add an order the
  // user feed just reported matched) and nothing would correct it until the
  // next event on that game.
  const userOrdersLiveUpdatedAt = new Map();
  function markUserOrdersLiveUpdate(gameID) {
    if (gameID == null) return;
    userOrdersLiveUpdatedAt.set(String(gameID), Date.now());
  }

  async function reseedUserOrders() {
    if (!userStore.user.id) return;
    if (userOrdersReseedInFlight) return userOrdersReseedInFlight;
    const now = Date.now();
    if (now - userOrdersReseedLastCall < USER_ORDERS_RESEED_MIN_INTERVAL_MS) {
      return;
    }
    userOrdersReseedLastCall = now;

    userOrdersReseedInFlight = (async () => {
      // Captured before the await: the response belongs to this user and is
      // no newer than this instant.
      const requestedBy = userStore.user.id;
      const requestedAt = now;
      try {
        const { get } = useAPI('user/getUnmatched/');
        const response = await get();
        // Logged out (or switched account) while the request was in flight:
        // reset() already cleared userOrders; this body describes the
        // previous session and must not be written over the new one.
        if (userStore.user.id !== requestedBy) return;
        // A failed/short-circuited request resolves undefined (useAPI swallows
        // it after reporting); only an actual array is the complete set, so
        // never clear the record on anything else.
        if (!Array.isArray(response?.unmatched)) return;

        const skipGameIDs = new Set();
        userOrdersLiveUpdatedAt.forEach((updatedAt, gameID) => {
          if (updatedAt > requestedAt) skipGameIDs.add(gameID);
        });

        const { entries, affected } = mergeUnmatchedIntoUserOrders(
          userOrders.value,
          response.unmatched,
          { skipGameIDs },
        );
        userOrders.value = entries;
        affected.forEach((gameID) => refreshAggregateOwnershipForGame(gameID));
      } catch (error) {
        console.info('reseedUserOrders failed', error);
      } finally {
        userOrdersReseedInFlight = null;
      }
    })();
    return userOrdersReseedInFlight;
  }

  function setSingleGameOrderSummary(payload) {
    singleGame.value.orderSummary = payload || [];
  }

  function setOddsComputed(value) {
    if (Number.isNaN(value)) {
      oddsComputed.value = '';
    } else {
      oddsComputed.value = value;
    }
  }

  function setEventSelected(value) {
    eventSelected.value = value;
  }

  function increasePage() {
    pagination.value.page += 1;
  }

  function setHasMore(value) {
    pagination.value.hasNextPage = value;
  }

  function setActivityFeedHistory(messages) {
    if (activityFeedHistoryPagination.value.page === 1) {
      activityFeedMessages.value = messages;
    } else {
      activityFeedMessages.value.push(...messages);
    }
  }

  function setBestOffers(offers) {
    bestOffers.value = offers;
  }

  // Fetched lazily by ChatActivity when the Best Offers tab is opened —
  // nothing else in the app renders this data, so nothing else pays for it.
  // The endpoint is Redis-cached server-side, so refetching per open is
  // cheap and keeps the feed fresh without a polling interval. The spinner
  // flag is only raised when the panel would otherwise be empty; a reopen
  // shows the previous list instantly while the refresh lands.
  async function fetchBestOffers() {
    // getBestOdds is auth-gated; useAPI would skip the request anyway and
    // return undefined. Bail explicitly so the caller's intent is readable
    // and we don't rely on a swallowed TypeError.
    if (!userStore.user?.isLoggedIn) {
      return;
    }
    try {
      if (bestOffers.value.length === 0) {
        bestOffersLoading.value = true;
      }
      const { get } = useAPI('/exchange/getBestOdds');
      const response = await get();
      // Only replace on a well-formed payload — a failed/partial response
      // must not blank out a list that is already on screen.
      if (Array.isArray(response?.odds)) {
        setBestOffers(response.odds);
      }
    } catch (error) {
      // Leave whatever was displayed; the next open retries.
    } finally {
      bestOffersLoading.value = false;
    }
  }

  function addActivityFeedMessage(message) {
    const currentMessageIndex = activityFeedMessages.value.findIndex(
      (msg) => msg.id === message.id,
    );
    if (currentMessageIndex !== -1) {
      activityFeedMessages.value.splice(currentMessageIndex, 1, message);
    } else {
      activityFeedMessages.value.unshift(message);
    }
  }

  function setActivityFeedHistoryPagination(payload) {
    activityFeedHistoryPagination.value = payload;
  }

  function setTopTradedOrderbooks(payload) {
    topTradedOrderbooks.value = payload;
    for (let index = 0; index < topTradedOrderbooks.value.length; index += 1) {
      if (
        topTradedOrderbooks.value[index].sport !== 'golf' &&
        Object.keys(topTradedOrderbooks.value[index].under).length === 0 &&
        Object.keys(topTradedOrderbooks.value[index].over).length === 0
      ) {
        topTradedOrderbooks.value[index].under = {
          0: [],
        };
        topTradedOrderbooks.value[index].over = {
          0: [],
        };
        topTradedOrderbooks.value[index].mainTotal = 0;
      }
      if (
        topTradedOrderbooks.value[index].sport !== 'fighting' &&
        Object.keys(topTradedOrderbooks.value[index].awaySpreads).length ===
          0 &&
        Object.keys(topTradedOrderbooks.value[index].homeSpreads).length === 0
      ) {
        topTradedOrderbooks.value[index].awaySpreads = {
          0: [],
        };
        topTradedOrderbooks.value[index].homeSpreads = {
          0: [],
        };
        topTradedOrderbooks.value[index].mainAwaySpread = 0;
        topTradedOrderbooks.value[index].mainHomeSpread = 0;
      }
    }
    emitter.emit('appWorkerUpdates', {
      type: 'UPDATE_GAMES',
      view: 'loggedIn',
      games: topTradedOrderbooks.value,
    });
  }

  function removeGame(payload) {
    const gameID = payload.id;
    if (gameID) {
      recordRemovedGame(gameID);
      const view = singleGameOrExchange.value;
      if (view === 'singleGame') {
        singleGame.value.games = singleGame.value.games.filter(
          (g) => g.id !== gameID,
        );
      } else if (view === 'loggedIn') {
        topTradedOrderbooks.value = topTradedOrderbooks.value.filter(
          (g) => g.id !== gameID,
        );
      } else {
        games.value = games.value.filter((g) => g.id !== gameID);
      }
    }
    userOrders.value = userOrders.value.filter(
      (order) => order.renderedGame.id !== gameID,
    );
    // A removal is live state too: an in-flight reseed must not re-add the
    // game's entry from its older snapshot.
    markUserOrdersLiveUpdate(gameID);
    emitter.emit('appWorkerUpdates', {
      type: 'REMOVE_GAMES',
      gameIds: [gameID],
    });
  }

  // Per-game timers that clear the price-flash deltas ~after the animation.
  // Tracked so a later update resets the timer instead of letting an earlier
  // update's timer clear a flash that a newer update just started (which made
  // exchange flashes cut off early / get skipped).
  const flashClearTimers = new Map();
  // How long the price-flash class stays applied. Must be >= the CSS animation
  // duration in _globals.scss (price-flash-tick, 1.9s) so the tick isn't cut.
  const FLASH_DURATION_MS = 2000;

  // oddsDelta is a nested bag of arrays (e.g. { awayMoneylines: [1,0,..],
  // awaySpreads: { '-3.5': [...] }, awayMoneylines1x2: { awayYes: [...] } }).
  // A "visible price move" = any array entry that is non-zero.
  function hasVisiblePriceMove(oddsDelta) {
    if (!oddsDelta) return false;
    const seen = new Set();
    const walk = (node) => {
      if (!node || typeof node !== 'object') return false;
      if (seen.has(node)) return false;
      seen.add(node);
      if (Array.isArray(node)) return node.some((v) => v === 1 || v === -1);
      return Object.values(node).some(walk);
    };
    return walk(oddsDelta);
  }

  function scheduleFlashClear(gameID) {
    const existing = flashClearTimers.get(gameID);
    if (existing) clearTimeout(existing);
    const id = setTimeout(() => {
      flashClearTimers.delete(gameID);
      clearDeltas(gameID);
    }, FLASH_DURATION_MS);
    flashClearTimers.set(gameID, id);
  }

  function clearDeltas(gameID) {
    if (singleGameOrExchange.value === 'exchange') {
      const gameIndex = games.value.findIndex((game) => game.id === gameID);
      if (gameIndex !== -1) {
        const game = games.value[gameIndex];
        game.oddsDelta = null;
        game.volumeDelta = null;
      }
    } else if (singleGameOrExchange.value === 'loggedIn') {
      const gameIndex = topTradedOrderbooks.value.findIndex(
        (game) => game.id === gameID,
      );
      if (gameIndex !== -1) {
        const game = topTradedOrderbooks.value[gameIndex];
        game.oddsDelta = null;
        game.volumeDelta = null;
      }
    } else if (singleGameOrExchange.value === 'singleGame') {
      const gameIndex = singleGame.value.games.findIndex(
        (game) => game.id === gameID,
      );
      if (gameIndex !== -1) {
        const game = singleGame.value.games[gameIndex];
        game.oddsDelta = null;
        game.volumeDelta = null;
      }
    }
  }

  function processOrderUpdate(orderObject) {
    if (!orderObject) return;

    // Resolve whether this game is on screen BEFORE normalizing: under the
    // firehose nearly every frame is for a game that is not in view, and
    // normalizing (which includes matching every level against the user's
    // open orders) is wasted work for all of them.
    const { gameID } = orderObject;
    if (!gameID) return;
    let oldGame;
    let oldGame2; // needed in case we the user is on take bet modal via best offers
    let gameIndex;
    if (singleGameOrExchange.value === 'exchange') {
      gameIndex = games.value.findIndex((game) => game.id === gameID);
      oldGame = games.value[gameIndex];
      if (bestOfferGame.value && bestOfferGame.value.id === gameID) {
        oldGame2 = bestOfferGame.value;
      }
    } else if (singleGameOrExchange.value === 'singleGame') {
      gameIndex = singleGame.value.games.findIndex(
        (game) => game.id === gameID,
      );
      oldGame = singleGame.value.games[gameIndex];
    } else if (singleGameOrExchange.value === 'loggedIn') {
      gameIndex = topTradedOrderbooks.value.findIndex(
        (game) => game.id === gameID,
      );
      oldGame = topTradedOrderbooks.value[gameIndex];
      if (bestOfferGame.value && bestOfferGame.value.id === gameID) {
        oldGame2 = bestOfferGame.value;
      }
    }
    if (!oldGame && !oldGame2) return;

    orderObject = normalizeOrderUpdate(orderObject);
    if (!orderObject) return;
    const { sideOrders } = orderObject;
    if (!sideOrders) return;

    const newGame = createNewGameFromOldGame(oldGame, orderObject);
    // Bump a per-game flash sequence whenever this update actually moved a
    // visible price. Cells fold flashSeq into their :key, so the flashing cell
    // remounts and the CSS tick animation REPLAYS from the start every time --
    // even on rapid back-to-back exchange updates where the .increase/.decrease
    // class was already applied (CSS won't restart an animation otherwise, which
    // is why exchange flashes were sometimes skipped or cut short).
    if (hasVisiblePriceMove(newGame.oddsDelta)) {
      newGame.flashSeq = (oldGame?.flashSeq ?? 0) + 1;
    } else if (oldGame) {
      newGame.flashSeq = oldGame.flashSeq ?? 0;
    }
    let newGame2;
    if (oldGame2) {
      newGame2 = createNewGameFromOldGame(oldGame2, orderObject);
    }
    if (singleGameOrExchange.value === 'exchange') {
      games.value.splice(gameIndex, 1, newGame);
    } else if (singleGameOrExchange.value === 'singleGame') {
      singleGame.value.games.splice(gameIndex, 1, newGame);
      if (singleGame.value.game && singleGame.value.game.id === newGame.id) {
        singleGame.value.game = newGame;
      }
    } else if (singleGameOrExchange.value === 'loggedIn') {
      if (oldGame) {
        topTradedOrderbooks.value.splice(gameIndex, 1, newGame);
      }
      if (oldGame2) {
        bestOfferGame.value = newGame;
      }
    }
    // Reset (not stack) the clear timer so an earlier update's timer can't wipe
    // a flash that this update just started.
    scheduleFlashClear(newGame.id);
  }

  function processGameUpdate(gameObj, sport) {
    // check if it is adding a new sport or league, if so run getSportsData
    const sportsInHeader = Object.keys(sportsNavigations.value.sportsAvailable);
    if (
      (!sportsInHeader?.includes(gameObj.sport) ||
        sportsNavigations.value?.sportsAvailable?.[gameObj.sport]?.includes(
          gameObj.league,
        )) &&
      sport
    ) {
      getSportsData(sport);
    }
    // if the game obj is in the single game that the user is on, hit fetch single game to refresh the data
    if (singleGameOrExchange.value === 'singleGame') {
      if (
        singleGame.value.game.id === gameObj.id ||
        singleGame.value.game.id === gameObj.id.parentId
      ) {
        emitter.emit('fetchSingleGameData', singleGame.value.game.id);
        return;
      }
    }
    // if in exchange view, find if the game object is in the array and replace it with the incoming on if so
    // or if the game object says the game is ended just remove it
    else if (singleGameOrExchange.value === 'exchange') {
      const gameIndex = games.value.findIndex((game) => game.id === gameObj.id);
      if (gameIndex === -1) return;

      if (gameObj.ended) {
        recordRemovedGame(gameObj.id);
        games.value.splice(gameIndex, 1);
        emitter.emit('appWorkerUpdates', {
          type: 'REMOVE_GAMES',
          gameIds: [gameObj.id],
        });
        return;
      }

      const existingGame = games.value[gameIndex];

      if (existingGame.start !== gameObj.start) {
        existingGame.start = gameObj.start;

        emitter.emit('appWorkerUpdates', {
          type: 'UPDATE_GAMES',
          view: singleGameOrExchange.value,
          games: [existingGame],
        });
      }
    }
    // Lobby (home) view: handle game lifecycle entirely from the socket --
    // no HTTP refetch and no global spinner. Spreads, totals and positions
    // are kept fresh by orderUpdate / positionUpdate on their own channels,
    // so we only touch start / live / ended here. If start moves, we ask
    // the expiry worker to reschedule; if the new start is in the past
    // the worker fires GAME_EXPIRED immediately and removeGame() splices
    // the card out.
    else if (singleGameOrExchange.value === 'loggedIn') {
      const gameIndex = topTradedOrderbooks.value.findIndex(
        (game) => game.id === gameObj.id,
      );
      if (gameIndex === -1) return;

      if (gameObj.ended) {
        recordRemovedGame(gameObj.id);
        topTradedOrderbooks.value.splice(gameIndex, 1);
        emitter.emit('appWorkerUpdates', {
          type: 'REMOVE_GAMES',
          gameIds: [gameObj.id],
        });
        return;
      }

      const existing = topTradedOrderbooks.value[gameIndex];
      const startChanged = existing.start !== gameObj.start;
      if (startChanged) existing.start = gameObj.start;
      if (existing.live !== gameObj.live) existing.live = gameObj.live;

      if (startChanged) {
        emitter.emit('appWorkerUpdates', {
          type: 'UPDATE_GAMES',
          view: singleGameOrExchange.value,
          games: [existing],
        });
      }
    }
  }

  function normalizeOrderUpdate(orderObject) {
    if (!orderObject) return null;
    if (Array.isArray(orderObject.sideOrders)) return orderObject;
    if (!Array.isArray(orderObject.priceLevels)) return null;

    // A level with no remaining volume is not a price; drop it so it can
    // never become a "best price", a $0.00 ladder row, or a no-op Max.
    // Sort best-for-the-taker first (higher American odds) so every
    // "first row is best" reader downstream stops depending on the
    // producer's emission order.
    const normalized = {
      ...orderObject,
      sideOrders: orderObject.priceLevels
        .filter((level) => Number(level.volume) > 0)
        .sort((a, b) => Number(b.odds) - Number(a.odds))
        .map((level) => priceLevelToSideOrder(orderObject, level)),
    };

    return normalized;
  }

  function priceLevelToSideOrder(orderObject, level) {
    // One level per odds per side is guaranteed by the renderer, so the side
    // identity plus the odds is already unique. The array index used to be
    // part of this id, which made every row below an inserted/removed level
    // change identity on each frame -- ladder selection, highlight, and
    // deselect-toggle (all keyed on row id) lost track mid-burst.
    const aggregateID = `price-level:${orderObject.gameID}:${orderObject.type}:${orderObject.market || ''}:${orderObject.side || ''}:${orderObject.participantID || ''}:${orderObject.spread ?? ''}:${orderObject.total ?? ''}:${level.odds}`;
    const volume = Number(level.volume) || 0;
    const odds = Number(level.odds) || 0;
    const sideOrder = {
      id: aggregateID,
      aggregateID,
      type: orderObject.type,
      sumUntaken: volume,
      odds,
      // Legacy per-order rows carried the maker's original stake in `bet`,
      // which for the taker is the win side of the trade. An aggregate level
      // has no per-order stake, so `bet` stays the level volume for the
      // consumers that treat it as "remaining", and the taker-win is carried
      // explicitly for the ones that need the real quantity (commission
      // display uses min(risk, win)).
      bet: volume,
      takerWin: volume * (convertAmericanToDecimal(odds) - 1),
      gameID: orderObject.gameID,
      takenRatio: 0,
      participantID: orderObject.participantID,
      market: orderObject.market,
      side: orderObject.side,
      OU: orderObject.OU,
      total: orderObject.total,
      spread: orderObject.spread,
      isUsers: false,
      priceFeedAggregate: true,
    };

    applyAggregateOwnership(sideOrder, orderObject, level);

    return sideOrder;
  }

  // Stamps (or clears) the user's ownership onto a synthesized aggregate side
  // order. The aggregate feed is anonymous, so cancel/edit from the book must
  // operate on the user's REAL order resolved from the user feed: we put its
  // real id and remaining stake on the row so those flows hit the API with
  // valid data instead of the synthetic aggregate values.
  function applyAggregateOwnership(sideOrder, orderObject, level) {
    const matchingOrders = userStore.user.id
      ? findUserOrdersForLevel(orderObject, level)
      : [];
    const userOrder = matchingOrders[0] ?? null;

    sideOrder.isUsers = Boolean(userOrder);

    if (userOrder) {
      sideOrder.createdBy = userStore.user.id;
      sideOrder.userOrderIDs = matchingOrders
        .map((order) => order.orderID)
        .filter(Boolean)
        .map(String);
      sideOrder.multipleUserOrders = sideOrder.userOrderIDs.length > 1;
      // The user's share of the level in the same taker-side units as the
      // level volume (`win` on an unmatched portion = remaining maker stake
      // converted to taker stake). Lets the take ladder show the level minus
      // the user's own volume instead of hiding the whole level.
      sideOrder.userSumUntaken = matchingOrders.reduce(
        (total, order) => total + (Number(order.win) || 0),
        0,
      );
      if (userOrder.orderID) {
        sideOrder.id = String(userOrder.orderID);
      }
      // The order summary's `risk` on an unmatched portion is the user's
      // remaining (unmatched) stake, which is what edit/cancel act on. The
      // stake is already the remainder, so takenRatio stays 0.
      const remainingStake = Number(userOrder.risk);
      if (Number.isFinite(remainingStake) && remainingStake > 0) {
        sideOrder.bet = remainingStake;
      }
      // The summary carries no expiry; game-start expiry is a placeholder
      // until resolveAggregateEditSession fetches the real order for editing.
      sideOrder.gameStartExpiry = true;
    } else {
      delete sideOrder.createdBy;
      delete sideOrder.gameStartExpiry;
      delete sideOrder.userOrderIDs;
      delete sideOrder.multipleUserOrders;
      delete sideOrder.userSumUntaken;
      sideOrder.id = sideOrder.aggregateID ?? sideOrder.id;
      sideOrder.bet = sideOrder.sumUntaken;
    }
  }

  function findUserOrdersForLevel(orderObject, level) {
    const userOrderSummary = userOrders.value.find(
      (summary) => summary.renderedGame?.id === orderObject.gameID,
    );
    if (!userOrderSummary) return [];

    const activeUserOrders = Array.isArray(userOrderSummary.unmatched)
      ? userOrderSummary.unmatched
      : [];

    return activeUserOrders.filter((order) =>
      userOrderMatchesPriceLevel(order, orderObject, level),
    );
  }

  // The user's own record of one open order, as the page load / position
  // updates / getUnmatched reseed left it: the same record that stamped the
  // book row as theirs.
  function findOwnOrderByID(gameID, orderID) {
    const summary = userOrders.value.find(
      (entry) => entry?.renderedGame?.id === gameID,
    );
    const open = Array.isArray(summary?.unmatched) ? summary.unmatched : [];
    return open.find((order) => String(order?.orderID) === orderID) ?? null;
  }

  // The edit endpoint aborts when the client's idea of the remaining stake is
  // more than this far from the real one (go-api edit/edit.go); the same
  // bound decides whether the in-memory record was too stale to edit from.
  const EDIT_STAKE_TOLERANCE = 1;

  // Second half of the instant-open path below: fetch the authoritative order
  // and swap it into the session the modal is already showing. `editable` is
  // reactive and is the very object the modal holds as its `session` prop, so
  // the callers need no change. Until this resolves the session is
  // expiryPending and the modal refuses to submit, because the in-memory
  // record has no expiry and every edit sends one.
  async function hydrateEditSession(editable, gameID, wantedID) {
    let realSession = null;
    try {
      realSession = await findOpenOfferByID(gameID, wantedID);
    } catch (error) {
      console.log('error resolving aggregate session', error);
    }
    if (!realSession) {
      editable.editUnavailable = true;
      return;
    }

    const book = flipRealSessionToBook(realSession);
    const realRemaining = Number(book.bet) * (1 - Number(book.takenRatio || 0));
    if (Math.abs(realRemaining - editable.bet) > EDIT_STAKE_TOLERANCE) {
      // The record missed a fill: the amounts on screen are wrong and the
      // server would abort the edit anyway. Close, and refresh the record so
      // the next open is right.
      editable.editUnavailable = true;
      reseedUserOrders();
      return;
    }

    // From here the session is exactly what the blocking path would have
    // handed the modal, so submit behaves identically.
    Object.assign(editable, book, { expiryPending: false });
  }

  // Resolves an aggregate (price feed) book row the user owns into the order
  // to edit. The row itself carries no per-order data. The order is first
  // taken from the user's own-orders record in memory so the modal opens at
  // once, then replaced by the authoritative order from the open-offers
  // endpoint (a slow, paged My Bets render) in the background; see
  // hydrateEditSession. TEMPORARY: once the record carries the order's expiry
  // the background fetch and the pending state go away entirely.
  // Returns one of:
  //   { status: 'ready', session }  - session to open the editor with; when
  //     session.expiryPending is true it must not be submitted yet, and when
  //     session.editUnavailable turns true the editor must close
  //   { status: 'multiple', gameID } - several of the user's orders share the
  //     level and no explicit targetOrderID was given; the caller must send
  //     the user to the open-offers list instead of silently picking one
  //   { status: 'unavailable', gameID } - the real order could not be
  //     fetched; the caller must NOT open the editor with synthetic data
  async function resolveAggregateEditSession(session, targetOrderID = null) {
    if (!session?.priceFeedAggregate) {
      return { status: 'ready', session };
    }
    // An aggregate row without a stamp has no real order behind it (stamping
    // raced or the order was just filled/cancelled); never open the editor
    // with synthetic data.
    if (!session.createdBy) {
      return { status: 'unavailable', gameID: session.gameID };
    }

    if (!targetOrderID && session.multipleUserOrders) {
      return { status: 'multiple', gameID: session.gameID };
    }

    const wantedID = String(targetOrderID ?? session.id);

    const provisional = ownOrderToBookSession(
      findOwnOrderByID(session.gameID, wantedID),
      { gameID: session.gameID, userID: userStore.user.id },
    );
    if (provisional) {
      const editable = reactive({
        ...provisional,
        expiryPending: true,
        editUnavailable: false,
      });
      // Deliberately not awaited: the modal opens now, the order follows.
      hydrateEditSession(editable, session.gameID, wantedID);
      return { status: 'ready', gameID: session.gameID, session: editable };
    }

    // No usable record of the order in memory: the blocking path, as before.
    try {
      const realSession = await findOpenOfferByID(session.gameID, wantedID);
      if (realSession) {
        return {
          status: 'ready',
          gameID: session.gameID,
          session: flipRealSessionToBook(realSession),
        };
      }
    } catch (error) {
      console.log('error resolving aggregate session', error);
    }

    return { status: 'unavailable', gameID: session.gameID };
  }

  // The open-offers endpoint returns at most 25 orders per page, so walk the
  // pages until the order is found. Bounded so malformed pagination data
  // can't loop forever.
  async function findOpenOfferByID(gameID, wantedID) {
    const { get } = useAPI('user/getOpenOffers/');
    const maxPages = 10;
    let numToSkip = 0;

    for (let page = 0; page < maxPages; page += 1) {
      const response = await get({ gameID, numToSkip });
      const openSessions = Array.isArray(response?.open) ? response.open : [];
      const realSession = openSessions.find(
        (offer) => String(offer.id) === wantedID,
      );
      if (realSession) return realSession;

      const nextSkip = response?.latestOrderIndex;
      if (!response?.allowNext || !hasValue(nextSkip) || nextSkip === numToSkip)
        return null;
      numToSkip = nextSkip;
    }

    return null;
  }

  // Same book-perspective conversion as OpenOffersModal.getFlippedSession:
  // the open-offers endpoint returns maker-perspective sessions, while
  // EditOrderModal expects the session as stored in the book.
  function flipRealSessionToBook(session) {
    return {
      id: session.id,
      bet: session.bet,
      createdBy: userStore.user.id,
      expiry: session.expiry,
      gameStartExpiry: session.gameStartExpiry === true || !session.expiry,
      sumUntaken: session.bet * (convertAmericanToDecimal(-session.odds) - 1),
      type: session.type,
      odds: -session.odds,
      spread: session.type === 'spread' ? -session.spread : null,
      total: session.type === 'total' ? session.total : null,
      OU:
        session.type === 'total'
          ? ['over', 'under'].find((ou) => ou !== session.OU)
          : null,
      participantID:
        session.type === 'total' ? null : session.otherParticipantID,
      takenRatio: session.takenRatio,
      market: session.market,
      side: session.side === 'yes' ? 'no' : 'yes',
      gameID: session.game?.id ?? session.gameID,
    };
  }

  function userOrderMatchesPriceLevel(order, orderObject, level) {
    // The user's order summary is rendered maker-side (odds negated by the
    // API), while the price feed carries the session as stored in the book,
    // so the user's order matches the mirrored book odds. +100 and -100 are
    // the same even-money price, so they compare equal.
    if (!oddsAreEquivalent(Number(order.odds), -Number(level.odds))) {
      return false;
    }

    const updateType = orderObject.type;
    const orderParticipantID =
      order.participantID ?? order.participant ?? order.side;
    const updateParticipantID =
      orderObject.participantID ?? orderObject.participant;

    if (hasValue(updateType) && order.type !== updateType) return false;

    if (!sameOptionalString(order.market, orderObject.market)) return false;

    if (updateType === 'spread') {
      if (
        !sameRequiredNumber(order.spread ?? order.number, orderObject.spread)
      ) {
        return false;
      }
      if (!sameRequiredString(orderParticipantID, updateParticipantID)) {
        return false;
      }
    } else if (updateType === 'total') {
      if (!sameRequiredNumber(order.total ?? order.number, orderObject.total)) {
        return false;
      }
      // Same maker/book mirroring as odds: the summary carries the maker's
      // over/under while the feed carries the session's, which is the flip.
      if (
        !sameRequiredString(
          flipOverUnder(order.OU ?? order.side),
          orderObject.OU ?? orderObject.side,
        )
      ) {
        return false;
      }
    } else if (updateType === 'moneyline') {
      if (!sameRequiredString(orderParticipantID, updateParticipantID)) {
        return false;
      }
    } else {
      if (!sameOptionalString(order.side, orderObject.side)) return false;
      if (!sameOptionalString(orderParticipantID, updateParticipantID)) {
        return false;
      }
    }

    return true;
  }

  function oddsAreEquivalent(left, right) {
    if (Math.abs(left) === 100 && Math.abs(right) === 100) return true;
    return left === right;
  }

  function flipOverUnder(value) {
    if (value === 'over') return 'under';
    if (value === 'under') return 'over';
    return value;
  }

  function sameRequiredNumber(left, right) {
    if (!hasValue(left) || !hasValue(right)) return false;
    return Number(left) === Number(right);
  }

  function sameRequiredString(left, right) {
    if (!hasValue(left) || !hasValue(right)) return false;
    return String(left) === String(right);
  }

  function sameOptionalString(left, right) {
    if (!hasValue(left) || !hasValue(right)) return true;
    return String(left) === String(right);
  }

  function hasValue(value) {
    return value !== undefined && value !== null && value !== '';
  }

  function refreshAggregateOwnershipForGame(gameID) {
    if (!gameID) return;

    refreshAggregateOwnershipInGame(bestOfferGame.value, gameID);
    refreshAggregateOwnershipInGame(singleGame.value.game, gameID);
    games.value.forEach((game) =>
      refreshAggregateOwnershipInGame(game, gameID),
    );
    topTradedOrderbooks.value.forEach((game) =>
      refreshAggregateOwnershipInGame(game, gameID),
    );
    singleGame.value.games.forEach((game) =>
      refreshAggregateOwnershipInGame(game, gameID),
    );
  }

  function refreshAggregateOwnershipInGame(game, gameID) {
    if (!game || game.id !== gameID) return;
    refreshAggregateOwnershipInValue(game);
  }

  function refreshAggregateOwnershipInValue(value) {
    if (Array.isArray(value)) {
      value.forEach((item) => refreshAggregateOwnershipInValue(item));
      return;
    }
    if (!value || typeof value !== 'object') return;

    if (value.priceFeedAggregate) {
      refreshAggregateSideOrderOwnership(value);
      return;
    }

    Object.values(value).forEach((child) =>
      refreshAggregateOwnershipInValue(child),
    );
  }

  function refreshAggregateSideOrderOwnership(sideOrder) {
    applyAggregateOwnership(sideOrder, sideOrder, {
      odds: sideOrder.odds,
      volume: sideOrder.sumUntaken,
    });
  }

  function processMatchedVolumeUpdate(update) {
    // The store only ever maintains facts (own volume + family aggregate);
    // the displayed value is derived in the UI via displayedMatchedVolume.
    // One match can affect a whole family, so apply the facts to every game
    // in the current view, not just the matched game.
    const facts = matchedVolumeFactsFromUpdate(update);

    let gamesInView = [];
    if (singleGameOrExchange.value === 'exchange') {
      gamesInView = games.value;
    } else if (singleGameOrExchange.value === 'singleGame') {
      gamesInView = singleGame.value.games;
    } else if (singleGameOrExchange.value === 'loggedIn') {
      gamesInView = topTradedOrderbooks.value;
    }

    gamesInView.forEach((game) => applyMatchedVolumeFacts(game, facts));
  }

  function setLoadingMoreGames(payload) {
    loadingMoreGames.value = payload;
  }

  function reset() {
    // PRO-123: the removed-game registry is an identity/session-scoped
    // memory ("this app saw these games end") — clear it at the same
    // boundary the rest of the store resets at, so a game whose ended flag
    // is later retracted isn't suppressed forever and nothing carries
    // across a logout/login on a shared browser.
    removedGameIds.clear();
    leagues.value = [];
    eventSelected.value = 'all';
    sportsNavigations.value = [];
    // Drop the shared-fetch cache with it, so the next caller re-fetches
    // instead of being handed the wiped nav.
    sportLeaguesFetchedAt = 0;
    sportLeaguesInflight = null;
    games.value = [];
    loadingMoreGames.value = false;
    loadingMoreRecentOrders.value = false;
    pagination.value = {
      totalDocs: null,
      offset: null,
      limit: 20,
      totalPages: null,
      page: 1,
      pagingCounter: null,
      hasPrevPage: null,
      hasNextPage: null,
      prevPage: null,
      nextPage: null,
      pages: [],
    };
    oddsComputed.value = '';
    userOrders.value = [];
    // Session-scoped reseed state goes with it: the live-update stamps
    // describe the old session's games, and the throttle window must not
    // suppress the reseed a login within 3s of a logout triggers. An
    // in-flight request is dropped by its own session guard.
    userOrdersLiveUpdatedAt.clear();
    userOrdersReseedLastCall = 0;
    userOrdersReseedInFlight = null;
    singleGame.value = {
      loading: false,
      game: {},
      orderSummary: [],
      childGames: [],
      games: [],
      navigation: [],
    };
    userOrdersLoading.value = false;
    singleGameOrExchange.value = '';
    activityFeedMessages.value = [];
    unreadActivityFeedMessages.value = 0;
    activityFeedHistoryPagination.value = {
      totalDocs: null,
      offset: null,
      limit: 20,
      totalPages: null,
      page: 1,
      pagingCounter: null,
      hasPrevPage: null,
      hasNextPage: null,
      prevPage: null,
      nextPage: null,
      pages: [],
    };
    topTradedOrderbooks.value = [];
  }

  function setBestOfferInfo(game, session) {
    bestOfferGame.value = game;
    bestOfferSession.value = session;
  }

  // See propsBetContext above. Pass null to clear.
  function setPropsBetContext(context) {
    propsBetContext.value = context;
  }

  // Login refreshes the nav. ApplicationLayout's own `logged-in` handler asks
  // for the same thing at the same moment; the shared fetch collapses both
  // into one request instead of two concurrent identical ones.
  emitter.on('loadSportsData', () => {
    fetchSportLeagues();
  });

  return {
    eventSelected,
    leagues,
    sportsNavigations,
    games,
    singleGame,
    userOrders,
    pagination,
    oddsComputed,
    userOrdersLoading,
    activityFeedMessages,
    unreadActivityFeedMessages,
    activityFeedHistoryPagination,
    loadingMoreGames,
    loadingMoreRecentOrders,
    topTradedOrderbooks,
    singleGameOrExchange,
    bestOffersLoading,
    bestOffers,
    bestOfferGame,
    bestOfferSession,
    propsBetContext,
    setPropsBetContext,
    matchType,
    leagueGames,
    leagueGamesLoading,
    fetchLeagueGames,
    setGames,
    pushGames,
    setSingleGame,
    setUserOrders,
    setEventSelected,
    setOddsComputed,
    setLeagues,
    getSportsData,
    fetchSportLeagues,
    applyLeaguesForSport,
    setActivityFeedHistory,
    addActivityFeedMessage,
    setActivityFeedHistoryPagination,
    setSingleGameOrderSummary,
    setTopTradedOrderbooks,
    processOrderUpdate,
    processMatchedVolumeUpdate,
    refreshAggregateOwnershipForGame,
    reseedUserOrders,
    markUserOrdersLiveUpdate,
    resolveAggregateEditSession,
    clearDeltas,
    removeGame,
    updateGame,
    setSingleGameOrExchange,
    setLoadingMoreGames,
    setGameOddsDelta,
    reset,
    processGameUpdate,
    setBestOffers,
    fetchBestOffers,
    setBestOfferInfo,
    increasePage,
    setHasMore,
    setMatchType,
  };
});
